import React, { useMemo, useState } from 'react'
import { 
  ConexionInterna, 
  TipoConexionInterna 
} from '../../types/connections'
import { 
  SplitterCompleto, 
  PuertoNAP 
} from '../../types/nodes'
import { posicionAColor } from '../../services/connectionService'
import { 
  Maximize2, 
  Minimize2, 
  ZoomIn, 
  ZoomOut,
  Info
} from 'lucide-react'

interface InternalConnectionsDiagramProps {
  nodoId: string
  tipoNodo: 'CIERRE' | 'NAP'
  nombreNodo?: string
  conexiones: ConexionInterna[]
  splitters: SplitterCompleto[]
  puertos?: PuertoNAP[]
  onSelectConexion?: (conexion: ConexionInterna) => void
  onSelectSplitter?: (splitter: SplitterCompleto) => void
  onSelectPuerto?: (puerto: PuertoNAP) => void
}

const COLOR_MAP: Record<string, string> = {
  azul: '#0066CC',
  naranja: '#FF6600',
  verde: '#00AA00',
  marron: '#8B4513',
  gris: '#808080',
  blanco: '#FFFFFF',
  rojo: '#CC0000',
  negro: '#1a1a1a',
  amarillo: '#FFD700',
  violeta: '#8B008B',
  rosa: '#FF69B4',
  turquesa: '#00CED1'
}

const TIPO_CONEXION_LABELS: Record<TipoConexionInterna, string> = {
  paso: 'Paso',
  fusion: 'Fusión',
  sangrado_splitter: 'Sangrado',
  salida_splitter: 'Salida SP',
  muerto: 'Muerto',
  reserva: 'Reserva'
}

const TIPO_CONEXION_COLORS: Record<TipoConexionInterna, string> = {
  paso: '#4CAF50',
  fusion: '#2196F3',
  sangrado_splitter: '#FF9800',
  salida_splitter: '#9C27B0',
  muerto: '#9E9E9E',
  reserva: '#795548'
}

export const InternalConnectionsDiagram: React.FC<InternalConnectionsDiagramProps> = ({
  nodoId,
  tipoNodo,
  nombreNodo,
  conexiones,
  splitters,
  puertos = [],
  onSelectConexion,
  onSelectSplitter,
  onSelectPuerto
}) => {
  const [zoom, setZoom] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [selectedElement, setSelectedElement] = useState<string | null>(null)
  const [showTooltip, setShowTooltip] = useState<{ x: number; y: number; content: string } | null>(null)

  const SVG_WIDTH = 800
  const SVG_HEIGHT = 500
  const PADDING = 40
  const CABLE_WIDTH = 120
  const SPLITTER_WIDTH = 100
  const SPLITTER_HEIGHT = 150
  const PUERTO_SIZE = 24

  const cablesUnicos = useMemo(() => {
    const cables = new Map<string, { id: string; tubos: number[]; direccion: 'entrada' | 'salida' }>()
    
    for (const conexion of conexiones) {
      const cableId = conexion.origen.cableId
      if (!cables.has(cableId)) {
        cables.set(cableId, { id: cableId, tubos: [], direccion: 'entrada' })
      }
      const cable = cables.get(cableId)!
      if (!cable.tubos.includes(conexion.origen.tubo)) {
        cable.tubos.push(conexion.origen.tubo)
      }
    }
    
    return Array.from(cables.values())
  }, [conexiones])

  const getColorHex = (color: string): string => {
    return COLOR_MAP[color.toLowerCase()] || '#888888'
  }

  const handleElementClick = (type: 'conexion' | 'splitter' | 'puerto', element: any) => {
    setSelectedElement(`${type}-${element.id || element.puerto}`)
    if (type === 'conexion' && onSelectConexion) onSelectConexion(element)
    if (type === 'splitter' && onSelectSplitter) onSelectSplitter(element)
    if (type === 'puerto' && onSelectPuerto) onSelectPuerto(element)
  }

  const handleMouseEnter = (x: number, y: number, content: string) => {
    setShowTooltip({ x, y, content })
  }

  const handleMouseLeave = () => {
    setShowTooltip(null)
  }

  const renderCableEntrante = (cable: typeof cablesUnicos[0], index: number, total: number) => {
    const startY = PADDING + (index * (SVG_HEIGHT - 2 * PADDING)) / Math.max(total, 1)
    const tuboHeight = 16
    
    return (
      <g key={`cable-${cable.id}`}>
        <rect
          x={PADDING}
          y={startY}
          width={CABLE_WIDTH}
          height={Math.max(cable.tubos.length * tuboHeight + 20, 50)}
          rx={5}
          fill="#f0f4f8"
          stroke="#94a3b8"
          strokeWidth={2}
        />
        <text
          x={PADDING + CABLE_WIDTH / 2}
          y={startY + 14}
          textAnchor="middle"
          className="text-xs font-semibold fill-gray-700"
        >
          {cable.id.substring(0, 12)}
        </text>
        
        {cable.tubos.sort((a, b) => a - b).map((tubo, tuboIdx) => {
          const tuboY = startY + 24 + tuboIdx * tuboHeight
          const tuboColor = posicionAColor(tubo)
          
          return (
            <g key={`tubo-${cable.id}-${tubo}`}>
              <rect
                x={PADDING + 8}
                y={tuboY}
                width={CABLE_WIDTH - 16}
                height={tuboHeight - 2}
                rx={3}
                fill={getColorHex(tuboColor)}
                opacity={0.8}
                stroke={getColorHex(tuboColor)}
                strokeWidth={1}
                className="cursor-pointer hover:opacity-100"
                onMouseEnter={(e) => handleMouseEnter(
                  e.clientX,
                  e.clientY,
                  `Tubo ${tubo} (${tuboColor})`
                )}
                onMouseLeave={handleMouseLeave}
              />
              <text
                x={PADDING + CABLE_WIDTH / 2}
                y={tuboY + 11}
                textAnchor="middle"
                className="text-xs font-medium pointer-events-none"
                fill={tuboColor === 'blanco' || tuboColor === 'amarillo' ? '#333' : '#fff'}
              >
                T{tubo}
              </text>
            </g>
          )
        })}
        
        <text
          x={PADDING + 4}
          y={startY - 4}
          className="text-xs fill-blue-600 font-medium"
        >
          ← Entrada
        </text>
      </g>
    )
  }

  const getSplitterBoxPos = (index: number, total: number) => {
    const centerX = SVG_WIDTH / 2
    const spacing = total > 1 ? (SVG_WIDTH - 300) / (total + 1) : 0
    const x = total > 1
      ? centerX - ((total - 1) * spacing) / 2 + index * spacing - SPLITTER_WIDTH / 2
      : centerX - SPLITTER_WIDTH / 2
    const y = PADDING + 40
    return { x, y }
  }

  const getSalidaDotPos = (splitter: SplitterCompleto, index: number, total: number, salidaIdx: number) => {
    const { x, y } = getSplitterBoxPos(index, total)
    const numSalidas = splitter.salidas.length
    const salidaHeight = Math.min(16, (SPLITTER_HEIGHT - 40) / numSalidas)
    const salidaY = y + 32 + salidaIdx * salidaHeight
    return { x: x + SPLITTER_WIDTH + 8, y: salidaY + salidaHeight / 2 }
  }

  const getEntradaDotPos = (index: number, total: number) => {
    const { x, y } = getSplitterBoxPos(index, total)
    return { x: x - 8, y: y + SPLITTER_HEIGHT / 2 }
  }

  /** Líneas de cascada: la salida de un splitter que alimenta la entrada de otro. */
  const renderCascadaLinks = () => {
    const total = splitters.length
    const idToIndex = new Map(splitters.map((s, i) => [s.id, i]))

    return (
      <>
        {splitters.map((splitter, idx) =>
          splitter.salidas.map((salida, salidaIdx) => {
            if (salida.destino?.tipo !== 'splitter_hijo' || !salida.destino.splitterDestinoId) return null
            const targetIdx = idToIndex.get(salida.destino.splitterDestinoId)
            if (targetIdx === undefined) return null

            const from = getSalidaDotPos(splitter, idx, total, salidaIdx)
            const to = getEntradaDotPos(targetIdx, total)
            const midY = Math.max(from.y, to.y) + 30

            return (
              <path
                key={`cascada-${splitter.id}-${salida.numero}`}
                d={`M ${from.x} ${from.y} C ${from.x + 30} ${midY}, ${to.x - 30} ${midY}, ${to.x} ${to.y}`}
                fill="none"
                stroke="#9333ea"
                strokeWidth={2}
                strokeDasharray="5,3"
                markerEnd="url(#arrowhead)"
                className="cursor-pointer"
                onMouseEnter={(e) =>
                  handleMouseEnter(e.clientX, e.clientY, `Cascada: Splitter ${splitter.id.slice(-6)} → Splitter ${salida.destino!.splitterDestinoId!.slice(-6)}`)
                }
                onMouseLeave={handleMouseLeave}
              />
            )
          })
        )}
      </>
    )
  }

  const renderSplitter = (splitter: SplitterCompleto, index: number, total: number) => {
    const { x, y } = getSplitterBoxPos(index, total)

    const numSalidas = splitter.salidas.length
    const salidaHeight = Math.min(16, (SPLITTER_HEIGHT - 40) / numSalidas)
    
    const isSelected = selectedElement === `splitter-${splitter.id}`
    
    return (
      <g 
        key={`splitter-${splitter.id}`}
        onClick={() => handleElementClick('splitter', splitter)}
        className="cursor-pointer"
      >
        <rect
          x={x}
          y={y}
          width={SPLITTER_WIDTH}
          height={SPLITTER_HEIGHT}
          rx={8}
          fill={isSelected ? '#e0f2fe' : '#f8fafc'}
          stroke={isSelected ? '#0284c7' : '#64748b'}
          strokeWidth={isSelected ? 3 : 2}
        />
        
        <rect
          x={x}
          y={y}
          width={SPLITTER_WIDTH}
          height={24}
          rx={8}
          fill={splitter.nivel === 1 ? '#3b82f6' : '#8b5cf6'}
        />
        <text
          x={x + SPLITTER_WIDTH / 2}
          y={y + 16}
          textAnchor="middle"
          className="text-xs font-bold fill-white"
        >
          {splitter.ratio}
        </text>
        
        <circle
          cx={x - 8}
          cy={y + SPLITTER_HEIGHT / 2}
          r={8}
          fill={getColorHex(splitter.entrada.colorFibra)}
          stroke="#333"
          strokeWidth={2}
        />
        <text
          x={x - 8}
          y={y + SPLITTER_HEIGHT / 2 + 4}
          textAnchor="middle"
          className="text-xs font-bold"
          fill={splitter.entrada.colorFibra === 'blanco' ? '#333' : '#fff'}
        >
          IN
        </text>
        
        {splitter.salidas.map((salida, idx) => {
          const salidaY = y + 32 + idx * salidaHeight
          const colorHex = getColorHex(salida.colorPigtail)
          
          return (
            <g key={`salida-${splitter.id}-${salida.numero}`}>
              <circle
                cx={x + SPLITTER_WIDTH + 8}
                cy={salidaY + salidaHeight / 2}
                r={6}
                fill={colorHex}
                stroke={salida.estado === 'ocupado' ? '#22c55e' : '#94a3b8'}
                strokeWidth={2}
                className="cursor-pointer"
                onMouseEnter={(e) => handleMouseEnter(
                  e.clientX,
                  e.clientY,
                  `Puerto ${salida.numero}: ${salida.estado} (${salida.atenuacion.toFixed(1)} dB)`
                )}
                onMouseLeave={handleMouseLeave}
              />
              <text
                x={x + SPLITTER_WIDTH + 20}
                y={salidaY + salidaHeight / 2 + 4}
                className="text-xs fill-gray-600"
              >
                {salida.numero}
              </text>
            </g>
          )
        })}
        
        <text
          x={x + SPLITTER_WIDTH / 2}
          y={y + SPLITTER_HEIGHT + 16}
          textAnchor="middle"
          className="text-xs fill-gray-500"
        >
          {splitter.id.substring(0, 10)}
        </text>
      </g>
    )
  }

  const renderPuertosNAP = () => {
    if (tipoNodo !== 'NAP' || puertos.length === 0) return null
    
    const startX = SVG_WIDTH - PADDING - 100
    const startY = PADDING + 40
    const columns = Math.ceil(puertos.length / 8)
    
    return (
      <g>
        <rect
          x={startX - 10}
          y={startY - 20}
          width={columns * 50 + 20}
          height={8 * (PUERTO_SIZE + 4) + 40}
          rx={8}
          fill="#fef3c7"
          stroke="#f59e0b"
          strokeWidth={2}
        />
        <text
          x={startX + (columns * 50) / 2}
          y={startY - 4}
          textAnchor="middle"
          className="text-xs font-bold fill-amber-700"
        >
          Puertos NAP
        </text>
        
        {puertos.map((puerto, idx) => {
          const col = Math.floor(idx / 8)
          const row = idx % 8
          const px = startX + col * 50
          const py = startY + 10 + row * (PUERTO_SIZE + 4)
          
          const isSelected = selectedElement === `puerto-${puerto.puerto}`
          const fillColor = puerto.estado === 'ocupado' 
            ? '#22c55e' 
            : puerto.estado === 'reservado'
            ? '#f59e0b'
            : puerto.estado === 'dañado'
            ? '#ef4444'
            : '#e2e8f0'
          
          return (
            <g 
              key={`puerto-${puerto.puerto}`}
              onClick={() => handleElementClick('puerto', puerto)}
              className="cursor-pointer"
            >
              <rect
                x={px}
                y={py}
                width={PUERTO_SIZE}
                height={PUERTO_SIZE}
                rx={4}
                fill={fillColor}
                stroke={isSelected ? '#0284c7' : '#64748b'}
                strokeWidth={isSelected ? 2 : 1}
              />
              <text
                x={px + PUERTO_SIZE / 2}
                y={py + PUERTO_SIZE / 2 + 4}
                textAnchor="middle"
                className="text-xs font-medium pointer-events-none"
                fill={puerto.estado === 'libre' ? '#64748b' : '#fff'}
              >
                {puerto.puerto}
              </text>
              
              {puerto.cliente && (
                <circle
                  cx={px + PUERTO_SIZE - 4}
                  cy={py + 4}
                  r={4}
                  fill="#3b82f6"
                  stroke="#fff"
                  strokeWidth={1}
                />
              )}
            </g>
          )
        })}
      </g>
    )
  }

  const renderConexiones = () => {
    return conexiones.map((conexion, idx) => {
      const startX = PADDING + CABLE_WIDTH
      const startY = PADDING + 60 + (idx % 8) * 20
      
      let endX = SVG_WIDTH / 2 - SPLITTER_WIDTH / 2 - 8
      let endY = PADDING + 40 + SPLITTER_HEIGHT / 2
      
      if (conexion.destino.tipo === 'puerto_nap') {
        const puertoNum = (conexion.destino as any).numeroPuerto
        endX = SVG_WIDTH - PADDING - 100
        endY = PADDING + 50 + ((puertoNum - 1) % 8) * (PUERTO_SIZE + 4) + PUERTO_SIZE / 2
      }
      
      const color = TIPO_CONEXION_COLORS[conexion.tipo]
      const isSelected = selectedElement === `conexion-${conexion.id}`
      
      const midX = (startX + endX) / 2
      const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`
      
      return (
        <g 
          key={`conexion-${conexion.id}`}
          onClick={() => handleElementClick('conexion', conexion)}
          className="cursor-pointer"
        >
          <path
            d={path}
            fill="none"
            stroke={isSelected ? '#0284c7' : color}
            strokeWidth={isSelected ? 3 : 2}
            strokeDasharray={conexion.tipo === 'muerto' ? '4,4' : undefined}
            opacity={conexion.tipo === 'muerto' ? 0.5 : 0.8}
          />
          
          {conexion.tipo === 'reserva' && (
            <circle
              cx={midX}
              cy={(startY + endY) / 2}
              r={6}
              fill="#795548"
              stroke="#fff"
              strokeWidth={1}
            />
          )}
        </g>
      )
    })
  }

  const renderLegend = () => {
    const legendItems = Object.entries(TIPO_CONEXION_LABELS).map(([tipo, label]) => ({
      tipo: tipo as TipoConexionInterna,
      label,
      color: TIPO_CONEXION_COLORS[tipo as TipoConexionInterna]
    }))
    
    return (
      <g transform={`translate(${SVG_WIDTH - 120}, ${SVG_HEIGHT - 100})`}>
        <rect
          x={-10}
          y={-10}
          width={130}
          height={legendItems.length * 16 + 20}
          rx={4}
          fill="rgba(255,255,255,0.95)"
          stroke="#e2e8f0"
        />
        <text x={0} y={6} className="text-xs font-semibold fill-gray-700">
          Leyenda
        </text>
        {legendItems.map((item, idx) => (
          <g key={item.tipo} transform={`translate(0, ${20 + idx * 16})`}>
            <line
              x1={0}
              y1={0}
              x2={20}
              y2={0}
              stroke={item.color}
              strokeWidth={2}
              strokeDasharray={item.tipo === 'muerto' ? '4,4' : undefined}
            />
            <text x={28} y={4} className="text-xs fill-gray-600">
              {item.label}
            </text>
          </g>
        ))}
      </g>
    )
  }

  return (
    <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}>
      <div className="flex items-center justify-between mb-2 px-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-700">
            Conexiones Internas - {nombreNodo || nodoId}
          </h3>
          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
            {tipoNodo}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}
            className="p-1 hover:bg-gray-100 rounded"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-500 w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(z => Math.min(2, z + 0.1))}
            className="p-1 hover:bg-gray-100 rounded"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1 hover:bg-gray-100 rounded ml-2"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      <div 
        className="overflow-auto border rounded-lg bg-gray-50"
        style={{ 
          maxHeight: isFullscreen ? 'calc(100vh - 100px)' : '400px',
          maxWidth: '100%'
        }}
      >
        <svg
          width={SVG_WIDTH * zoom}
          height={SVG_HEIGHT * zoom}
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          className="bg-white"
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
            </marker>
          </defs>
          
          <text
            x={SVG_WIDTH / 2}
            y={20}
            textAnchor="middle"
            className="text-sm font-bold fill-gray-700"
          >
            Diagrama de Conexiones - {tipoNodo === 'NAP' ? 'Caja NAP' : 'Cierre'}
          </text>
          
          {cablesUnicos.map((cable, idx) => 
            renderCableEntrante(cable, idx, cablesUnicos.length)
          )}
          
          {splitters.map((splitter, idx) =>
            renderSplitter(splitter, idx, splitters.length)
          )}

          {renderCascadaLinks()}

          {renderPuertosNAP()}
          
          {renderConexiones()}
          
          {renderLegend()}
          
          {conexiones.length === 0 && splitters.length === 0 && (
            <g>
              <rect
                x={SVG_WIDTH / 2 - 100}
                y={SVG_HEIGHT / 2 - 30}
                width={200}
                height={60}
                rx={8}
                fill="#f1f5f9"
                stroke="#cbd5e1"
                strokeDasharray="4,4"
              />
              <text
                x={SVG_WIDTH / 2}
                y={SVG_HEIGHT / 2}
                textAnchor="middle"
                className="text-sm fill-gray-500"
              >
                Sin conexiones configuradas
              </text>
              <text
                x={SVG_WIDTH / 2}
                y={SVG_HEIGHT / 2 + 18}
                textAnchor="middle"
                className="text-xs fill-gray-400"
              >
                Use el editor de sangrado para agregar
              </text>
            </g>
          )}
        </svg>
      </div>
      
      {showTooltip && (
        <div
          className="fixed z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg pointer-events-none"
          style={{ left: showTooltip.x + 10, top: showTooltip.y - 30 }}
        >
          {showTooltip.content}
        </div>
      )}
      
      <div className="mt-2 px-2 flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Info className="w-3 h-3" />
          <span>Click en elementos para ver detalles</span>
        </div>
        <span>|</span>
        <span>Conexiones: {conexiones.length}</span>
        <span>|</span>
        <span>Splitters: {splitters.length}</span>
        {tipoNodo === 'NAP' && (
          <>
            <span>|</span>
            <span>Puertos: {puertos.filter(p => p.estado === 'ocupado').length}/{puertos.length}</span>
          </>
        )}
      </div>
    </div>
  )
}

export default InternalConnectionsDiagram
